import { useCallback, useEffect, useState } from "react";
import { Button, Input, Switch, Textarea } from "@heroui/react";
import { Trans, useTranslation } from "react-i18next";
import { useAzureFuncApp, useEntraUser, usePlatformStatus } from "@VinciDiane/core";
import MaintenanceLogo from "./MaintenanceLogo";
import config from "@/config";

// Rôle IEP global (pas de project_id) de l'utilisateur courant, pour savoir s'il
// peut piloter la maintenance. Même endpoint que RoleProvider (service-smart-implant),
// appelé ici directement : RoleProvider n'est pas exporté publiquement par ce package.
// Posé par le bouton "Accéder à la plateforme quand même" ci-dessous, lu par
// AppContent (App.jsx) pour ne pas renvoyer un admin qui a choisi de passer
// outre. sessionStorage (pas de provider commun entre ce composant, rendu
// hors router lors du blocage global, et AppContent) ; nettoyé dès que le
// statut repasse à enabled=false (cf. App.jsx), pour ne couvrir que le
// blocage en cours.
export const MAINTENANCE_BYPASS_KEY = "iep_maintenance_bypass";

function useIepRole() {
  const { launch_request } = useAzureFuncApp();
  const { getGraphToken } = useEntraUser();
  const [role, setRole] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [status, result] = await launch_request(
        `${config.apiIepUri}/roles/me`,
        { graph_token: await getGraphToken() },
        { method: "GET", alertError: false }
      );
      if (!cancelled && status === 200) {
        setRole((result?.role ?? result?.user?.role ?? "client").toLowerCase());
      }
    })();
    return () => { cancelled = true; };
  }, [launch_request, getGraphToken]);

  return role;
}

// Navigation en URL brute (pas useNavigate) : App.jsx rend ce composant à la
// fois comme route normale et directement hors du router (blocage global,
// cf. AppContent) — useNavigate planterait dans ce second cas.
//
// requireAdmin : réservé à la route /maintenance (visible et éditable
// uniquement par un admin IEP, blocage en cours ou non). Absent lors du
// blocage global automatique (AppContent) : tout le monde doit voir le
// message, seul le panneau d'édition reste filtré sur isAdmin.
const PageMaintenance = ({ requireAdmin = false }) => {
  const { t } = useTranslation('home');
  const role = useIepRole();
  const isAdmin = role === "admin";
  const { status, updateStatus } = usePlatformStatus();
  const showBlocked = Boolean(status?.enabled);

  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftEta, setDraftEta] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftEnabled(Boolean(status?.enabled));
    setDraftEta(status?.eta ? status.eta.slice(0, 16) : "");
    setDraftReason(status?.reason ?? "");
  }, [status?.enabled, status?.eta, status?.reason]);

  const messages = t('maintenance.messages', { returnObjects: true });
  const [randomIndex] = useState(() => Math.floor(Math.random() * (messages?.length ?? 1)));

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateStatus({
        enabled: draftEnabled,
        eta: draftEta ? new Date(draftEta).toISOString() : null,
        reason: draftReason,
      });
    } finally {
      setSaving(false);
    }
  }, [updateStatus, draftEnabled, draftEta, draftReason]);

  // /maintenance (requireAdmin) : rôle pas encore connu -> on attend avant de
  // décider, pour ne jamais laisser flasher le panneau à un non-admin.
  // Blocage global automatique (AppContent, !requireAdmin) : le message doit
  // s'afficher à tout le monde tout de suite, role encore null ou pas.
  if (requireAdmin && role === null) return null;
  if (requireAdmin ? !isAdmin : !showBlocked && !isAdmin) {
    window.location.replace("/");
    return null;
  }

  return (
    <div className="w-full h-screen overflow-y-auto flex items-center justify-center p-4 sm:p-6">
      {/* RootLayout pose ce fond pour les routes normales, mais ce composant est
          aussi rendu directement par AppContent lors du blocage global, hors
          RootLayout : sans ça, fond blanc uni dans ce cas. */}
      <div className="bg-hero-section-centered-navbar fixed z-[-1] w-screen h-screen top-0 left-0"></div>
      <div className={`w-full max-w-4xl grid grid-cols-1 gap-4 items-center ${isAdmin ? "lg:grid-cols-2 lg:gap-8" : ""}`}>
        <div className="flex flex-row items-center justify-center gap-4 lg:flex-col lg:text-center">
          <MaintenanceLogo className="w-56 h-56 scale-125 sm:w-56 sm:h-56 shrink-0" />
          <div>
            <h1 className="h2 text-2xl! sm:text-3xl!">
              {showBlocked ? "Maintenance en cours" : "Service en ligne"}
            </h1>
            <h1 className="h3 subtitle text-base! sm:text-lg!">
              {status?.reason || (
                <Trans i18nKey={`maintenance.messages.${randomIndex}`} ns="home" components={[<br key="br" />]} />
              )}
            </h1>
          </div>
        </div>

        {isAdmin && (
          <div className="glass light rounded-3xl p-4 sm:p-6 text-left flex flex-col gap-3">
            <Switch isSelected={draftEnabled} onValueChange={setDraftEnabled} color="danger">
              Maintenance {draftEnabled ? "activée" : "désactivée"}
            </Switch>
            <Input
              type="datetime-local"
              label="Heure de reprise estimée"
              labelPlacement="outside"
              size="sm"
              value={draftEta}
              onValueChange={setDraftEta}
              className="max-w-full sm:max-w-[240px]"
            />
            <Textarea
              label="Raison (affichée aux utilisateurs)"
              labelPlacement="outside"
              size="sm"
              minRows={2}
              value={draftReason}
              onValueChange={setDraftReason}
            />
            <div className="flex gap-2">
              <Button size="sm" color="primary" isLoading={saving} onPress={handleSave}>
                Enregistrer
              </Button>
              {status?.enabled && (
                <Button
                  size="sm"
                  color="danger"
                  variant="flat"
                  onPress={() => {
                    sessionStorage.setItem(MAINTENANCE_BYPASS_KEY, "1");
                    window.location.assign("/");
                  }}
                >
                  Accéder à la plateforme quand même
                </Button>
              )}
            </div>
            {status?.updated_at && (
              <p className="text-xs text-default-400">
                Dernière modification par {status.updated_by ?? "inconnu"} le{" "}
                {new Date(status.updated_at).toLocaleString("fr-FR")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageMaintenance;
